import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api, type Id } from "../lib/api";
import { useCompany } from "../lib/company";
import { useAccess } from "../lib/access";
import { Button, Card } from "../components/ui";
import { caughtErrorMessage } from "../domain/errors";
import { formatAuDate } from "../domain/time";

export function ObligationSets() {
  const { companyId } = useCompany();
  const { canWrite } = useAccess();
  const sets = useQuery(api.obligationSets.listForCompany, companyId ? { companyId, limit: 50 } : "skip");
  const documents = useQuery(api.policies.listForCompany, companyId ? { companyId } : "skip");
  const remove = useMutation(api.obligationSets.remove);
  const [confirming, setConfirming] = useState<Id<"obligationSets"> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState("");
  const deleteSet = async () => {
    if (!companyId || !confirming) return;
    setBusy(true); setError(null);
    try { await remove({ companyId, obligationSetId: confirming }); setConfirming(null); }
    catch (e) { setError(caughtErrorMessage(e, "Could not delete obligation set")); }
    finally { setBusy(false); }
  };
  return <div className="mx-auto max-w-5xl space-y-5 px-6 py-7">
    <h1 className="font-serif text-3xl font-semibold">Saved obligation sets</h1>
    <p className="text-slate-600">Reuse selected clauses in document mapping, or return to their source search.</p>
    <Link className="text-[var(--accent-dark)] underline" to="/browse">Find obligations in the source register</Link>
    {canWrite && <label className="block text-sm">Document to map against
      <select aria-label="Document to map against" value={documentId} onChange={e => setDocumentId(e.target.value)} className="ml-3 rounded border p-2">
        <option value="">Choose a document</option>
        {(documents ?? []).filter(document => document.status !== "archived").map(document => <option key={document._id} value={document._id}>{document.title}</option>)}
      </select>
    </label>}
    {error && <p role="alert" className="text-rose-700">{error}</p>}
    {sets === undefined ? <p role="status">Loading saved sets…</p> : sets.length === 0 ? <Card className="p-6">No saved sets yet. Select clauses in a source-register search to create one.</Card> :
      <ul className="space-y-3">{sets.map(set => <li key={set._id}><Card className="space-y-3 p-5">
        <h2 className="text-lg font-semibold">{set.name}</h2>
        <p className="text-sm text-slate-600">{set.itemCount} obligations · Updated {formatAuDate(set.updatedAt)} · Query: {set.query}</p>
        <div className="flex flex-wrap items-center gap-4">
          <Link to={`/browse?search=${encodeURIComponent(set.query)}`} className="underline">Reopen search</Link>
          {canWrite && documentId && <Link to={`/documents/${documentId}?obligationSet=${set._id}`} className="underline">Use in document mapping</Link>}
          {canWrite && (confirming === set._id ? <>
            <span>Delete this saved set?</span><Button disabled={busy} onClick={() => void deleteSet()}>Confirm delete</Button>
            <Button variant="outline" disabled={busy} onClick={() => setConfirming(null)}>Cancel</Button>
          </> : <Button variant="outline" onClick={() => setConfirming(set._id)}>Delete</Button>)}
        </div>
      </Card></li>)}</ul>}
  </div>;
}
